import {useEffect, useMemo, useRef, useState} from 'react'

type MultiSelectProps = {
  label?: string
  name?: string
  placeholder: string
  options: {value: any; label: string}[]
  selected?: {value: any; label: string}[]
  onSelect: (values: {value: any; label: string}[]) => void
  disabled?: boolean
  required?: boolean
  searchFn?: (val: string) => void
  createFn?: (val: string) => void
  error?: boolean
  className?: string
}

export const MultiSelect = (props: MultiSelectProps) => {
  const [selected, setSelected] = useState<{value: any; label: string}[]>([])
  const [value, setValue] = useState('')
  const [localOptions, setLocalOptions] = useState<any>()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const options = useMemo(() => {
    const source = localOptions || props.options
    return source.filter((opt:any) => !selected.some((s) => s.value === opt.value))
  }, [props.options, localOptions, selected])
  
  useEffect(() => {
    if (props.selected) {
      setSelected(props.selected)
    }
  }, [props.selected])

  const handleSelect = (item: {value: any; label: string}) => {
    const newSelected = selected.some((s) => s.value === item.value)
      ? selected.filter((s) => s.value !== item.value)
      : [...selected, item]

    setSelected(newSelected)
    props.onSelect(newSelected)
  }

  const localSearchFn = (val: string) => {
    setLocalOptions(
      props.options.filter((option) => option.label.toLowerCase().includes(val.toLowerCase()))
    )
  }

  const removeSelected = (valueToRemove: any) => {
    const newSelected = selected.filter((item) => item.value !== valueToRemove)
    setSelected(newSelected)
    props.onSelect(newSelected)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      <style>{`
  .select-container {
    position: relative;
  }

  .select-input {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background-color: #f5f8fa;
    border: 1px solid transparent;
    border-radius: 0.475rem;
    height: auto;
    cursor: pointer;
    transition: background-color 0.2s ease;
    min-height: 46px;
  }

  .select-input:focus-within {
    background-color: #eef3f7;
  }

  .select-input input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 16px;
    background: transparent;
    color: #3f4254;
    flex: 1;
    min-width: 50px;
  }

  .select-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: #fff;
    border-radius: 6px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    margin-top: 4px;
    max-height: 240px;
    overflow-y: auto;
  }

  .select-item {
    padding: 10px 17px;
    font-size: 15px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    color: #3f4254;
  }

  .select-item:hover {
    background-color:#F1FAFF;
    color: #3699FF;
  }

  .selected-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-right: 8px;
  }

  .selected-tag {
    background-color: #e9ecef;
    border-radius: 4px;
    padding: 2px 6px;
    display: flex;
    align-items: center;
    font-size: 14px;
  }

  .selected-tag-remove {
    margin-left: 4px;
    cursor: pointer;
    color: #6c757d;
  }

  .selected-tag-remove:hover {
    color: #dc3545;
  }
`}</style>

      <div className='select-container' ref={dropdownRef}>
        {/* Input Box */}
        <div className='select-input' onClick={() => !props.disabled && setIsOpen(!isOpen)}>
          <div className='selected-tags'>
            {selected.map((item) => (
              <div key={item.value} className='selected-tag'>
                {item.label}
                <span
                  className='selected-tag-remove'
                  onClick={(e) => {
                    e.stopPropagation()
                    removeSelected(item.value)
                  }}
                >
                  ×
                </span>
              </div>
            ))}
          </div>
          <input
            className='select-form-control'
            placeholder={selected.length === 0 ? props.placeholder : ''}
            value={value}
            disabled={props.disabled}
            onChange={(e) => {
              const val = e.target.value
              props.searchFn ? props.searchFn(val) : localSearchFn(val)
              setValue(val)
            }}
            onClick={(e) => {
              e.stopPropagation()
              if (!props.disabled) setIsOpen(true)
            }}
          />
          <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </div>
        {/* Dropdown List */}
        {isOpen && (
          <div className='select-menu rounded border border-gray-300 shadow-lg'>
            {options.length > 0 ? (
              options.map((item: any) => (
                <div
                  key={item.value}
                  className={`select-item ${
                    selected.some((s) => s.value === item.value)
                      ? 'bg-primary/10 text-primary font-medium'
                      : ''
                  }`}
                  onClick={() => handleSelect(item)}
                >
                  {item.label}
                  {selected.some((s) => s.value === item.value) && <i className='bi bi-check'></i>}
                </div>
              ))
            ) : (
              <>
                <div className='px-4 py-2 text-gray-500'>No record</div>
                {props.createFn && value && (
                  <div
                    className='px-4 py-2 cursor-pointer'
                    onClick={() => {
                      props.createFn?.(value)
                      setIsOpen(false)
                    }}
                  >
                    Create "{value}"
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}
